import * as React from 'react'
import { all, call, put, select, takeEvery } from 'redux-saga/effects'
import api from '../../../services/api.services'
import {
  ActivityProgress,
  CustomType,
  ExportChatImages$Response,
  ExportChatMessages$Response,
  ExportGoalResults$Request,
  ExportGoalResults$Response,
  GetGoalCountryProgress$Response,
  GetGoalGroupProgress$Response,
  GetGoalIndividualProgress$Response,
  GetGoalsWithProgress$Response,
  GetLeaderboardCountries$Response,
  GetLeaderboardGroups$Response,
  GetLeaderboardPeople$Response,
  GetSingleChallengeWithProgress$Request,
  GetSingleChallengeWithProgress$Response,
  GetUserGoalProgress$Response,
  GoalId,
  GoalWithProgress,
  Lang,
  LeaveGoal$Request,
  ProgressType,
  Role,
  UserId,
  UserProfileDto,
} from '../../../services/api.types'
import {
  getChatMessagesExport,
  getChatImagesExport,
  getCurrentCompanyGoal,
  getCurrentCompanyGoalSuccess,
  getCurrentGoalActivityList,
  getCurrentGoalActivityListSuccess,
  getCurrentGoalExport,
  getCurrentLeaderboard,
  getSingleChallengeWithProgress,
  getSingleChallengeWithProgressSuccess,
  joinGoal,
  leaveGoal,
  cancelGetChallengesWithProgress,
} from './ActivityActions'
import { Action } from 'redux-actions'
import { getUserDto, getUserIntegrationScopes, hasRole } from '../../../common/auth/authSelectors'
import { ActivityView } from './ActivityReducer'
import { getActivityFilter, getActivityView, getGroupSum, getGroupView, getUserGoal } from './ActivitySelectors'
import { download } from '../../../common/helpers/downloadHelpers'
import { openUrl } from '../../../common/helpers/routeHelpers'
import { INotification } from '../../../common/notifications/NotificationReducers'
import { t } from 'ttag'
import { addNotification, clearNotification } from '../../../common/notifications/NotificationActions'
import { getCompanyDivisionsSuccess, getUserCompanyCountriesSuccess } from '../../Settings/store/SettingsActions'
import { convertToMultipleGoalsFormat, getGroupSumFromActivityView } from '../../../common/helpers/challenges'
import { scrollToTop } from '../../../common/helpers/scroll'
import YumSetupActivityDataWrapper from '../../../components/YumSetupActivityData/YumSetupActivityDataWrapper'
import { getErrorMsg } from '../../../common/helpers/translations'
import {
  isActivityIndexType,
  isCollectiveType,
  isManualEntryOnlyType,
  isStepsType,
} from '../../../common/helpers/challenges'
import YumChooseIntegrationBtnWrapper from '../../../components/YumChooseIntegrationBtn/YumChooseIntegrationBtnWrapper'
import { currentLang } from '../../../i18nInit'
import buildVersion from '../../../version.json'
import { setLoadingJoinGoal, setLoadingJoinGoalDone } from '../../../common/loading/loadingActions'
import { getCustomTypeWithLegacy } from '../../../services/CustomTypes'
import { refreshChallengeResults } from '../../Progress/store/ProgressActions'
import { convertUTCToLocalDate } from '../../../common/helpers/date'
import {
  setLoadingChatImagesExport,
  setLoadingChatImagesExportDone,
  setLoadingExport,
  setLoadingExportDone,
} from '../../../common/loading/loadingActions'
import { getDivisionCategoriesSuccess } from '../../TeamManagement/store/TeamManagementActions'
import { announce } from '../../../components/a11y/useAnnouncer'

const { version } = buildVersion || { version: '' }

const getUserDetails = (userId: UserId, activityProgress: { [p: string]: ActivityProgress }) => {
  const userData = Object.entries(activityProgress || {}).find(([compareID]) => compareID === userId)
  const [, userDetails] = userData || ['', null]
  return userDetails as ActivityProgress
}

// AbortController reference for cancelling the getChallengesWithProgress request
let challengesAbortController: AbortController | null = null

function* sagaGetCurrentGoalActivityList({ payload }: Action<any>) {
  //console.log('sagaGetCurrentGoalActivityList')
  // Leaderboard (with token auth).
  /*if (payload.token) {
    yield put({ type: String(getCurrentLeaderboard), payload })
    return
  }*/

  const { goalId, divisionId } = payload || {}
  let { activityFilter, activityView, groupView, groupSum } = payload || {}
  if (!activityFilter) {
    // @ts-ignore
    activityFilter = yield select(getActivityFilter, goalId)
  }
  if (!activityView) {
    // @ts-ignore
    activityView = yield select(getActivityView, goalId)
  }
  if (!groupView) {
    // @ts-ignore
    groupView = yield select(getGroupView, goalId)
  }
  if (groupSum === undefined) {
    // @ts-ignore
    groupSum = yield select(getGroupSum, goalId)
  }

  try {
    const outputPayload = {
      goalId,
      activityFilter,
      userProgress: undefined,
      activityProgress: undefined,
      divisionActivityProgress: undefined,
      countryActivityProgress: undefined,
      userIsInActivityList: false,
    } as any

    const userDto: UserProfileDto = yield select(getUserDto)
    const userId = userDto.userId

    // TODO: Do this only for the legacy ranking
    if (activityView === ActivityView.INDIVIDUAL || activityView === ActivityView.COLLECTIVE) {
      const payload: GetGoalIndividualProgress$Response = yield call(api.activity.getGoalIndividualProgress, {
        ...activityFilter,
        goalId,
        skipJoin: true,
      })
      const activityProgress = payload.activityProgress
      outputPayload.userIsInActivityList = Object.keys(activityProgress || {}).some((compareID) => compareID === userId)
      outputPayload.activityProgress = activityProgress ? activityProgress : []
      outputPayload.activityProgress = activityProgress
    } else if (activityView === ActivityView.GROUP) {
      const payload: GetGoalGroupProgress$Response = yield call(api.activity.getGoalGroupProgress, {
        groupView,
        goalId,
        sum: groupSum,
      })
      outputPayload.groupView = groupView
      const activityProgress = payload.divisionActivityProgress
      /*if (groupView === GroupView.COUNTRIES_MERGED) {
        outputPayload.userIsInActivityList = Object.values(activityProgress || {}).some(
          (row) => row.divisionId === divisionId, //userDto.divisionId,
        )
      } else {
        outputPayload.userIsInActivityList = Object.values(activityProgress || {}).some(
          (row) => row.divisionId === divisionId && row.country === userDto.country,
        )
      }*/
      outputPayload.divisionActivityProgress = activityProgress
    } else {
      const payload: GetGoalCountryProgress$Response = yield call(api.activity.getGoalCountryProgress, {
        goalId,
        sum: groupSum,
      })
      const activityProgress = payload.countryActivityProgress
      /*outputPayload.userIsInActivityList = Object.values(activityProgress || {}).some(
        (row) => row.country === userDto.country,
      )*/
      outputPayload.countryActivityProgress = activityProgress
    }

    //const isAdmin = yield select(hasRole, Role.ADMIN)
    //if (!isAdmin) {
    // TODO: We should check this based on current goal/companyId.
    const isUser: boolean = yield select(hasRole, Role.USER)
    if (isUser) {
      // If user is in activity list, we can get the progress from there, otherwise we need a separate call.
      if (
        (activityView === ActivityView.INDIVIDUAL || activityView === ActivityView.COLLECTIVE) &&
        outputPayload.userIsInActivityList
      ) {
        outputPayload.userProgress = getUserDetails(userId, outputPayload.activityProgress)
      } else {
        const { activityProgress: userActivityProgress }: GetUserGoalProgress$Response = yield call(
          api.activity.getUserGoalProgress,
          { goalId },
        )
        outputPayload.userProgress = getUserDetails(userId, userActivityProgress)
      }
    } else {
      outputPayload.userProgress = null
    }

    yield put({ type: String(getCurrentGoalActivityListSuccess), payload: outputPayload })
  } catch (e: any) {
    console.error('GetActivityListFailed: ', e)
  }
}

function* sagaGetCurrentLeaderboard({ payload }: Action<any>) {
  const { goalId, token } = payload || {}
  let { activityFilter, activityView, groupView, groupSum } = payload || {}
  if (!activityFilter) {
    // @ts-ignore
    activityFilter = yield select(getActivityFilter, goalId)
  }
  if (!activityView) {
    // @ts-ignore
    activityView = yield select(getActivityView, goalId)
  }
  if (!groupView) {
    // @ts-ignore
    groupView = yield select(getGroupView, goalId)
  }
  if (groupSum === undefined) {
    // @ts-ignore
    groupSum = yield select(getGroupSum, goalId)
  }

  try {
    const outputPayload = {
      goalId,
      activityFilter,
      userProgress: null,
      activityProgress: undefined,
      divisionActivityProgress: undefined,
      countryActivityProgress: undefined,
      userIsInActivityList: false,
    } as any

    let data
    if (activityView === ActivityView.INDIVIDUAL || activityView === ActivityView.COLLECTIVE) {
      const response: GetLeaderboardPeople$Response = yield call(api.activity.getLeaderboardPeople, {
        ...activityFilter,
        goalId,
        skipJoin: true,
        token,
      })
      outputPayload.activityProgress = response.activityProgress
      data = response
    } else if (activityView === ActivityView.GROUP) {
      const response: GetLeaderboardGroups$Response = yield call(api.activity.getLeaderboardGroups, {
        groupView,
        goalId,
        sum: groupSum,
        token,
      })
      outputPayload.groupView = groupView
      outputPayload.divisionActivityProgress = response.divisionActivityProgress
      data = response
    } else {
      const response: GetLeaderboardCountries$Response = yield call(api.activity.getLeaderboardCountries, {
        goalId,
        sum: groupSum,
        token,
      })
      outputPayload.countryActivityProgress = response.countryActivityProgress
      data = response
    }

    yield put({ type: String(getCompanyDivisionsSuccess), payload: data.divisionDtos })
    yield put({ type: String(getUserCompanyCountriesSuccess), payload: data.companyCountries })
    // Convert old collective challenges to new format
    const convertedGoals = data.goals ? data.goals.map(convertToMultipleGoalsFormat) : []
    yield put({ type: String(getCurrentCompanyGoalSuccess), payload: convertedGoals })

    yield put({ type: String(getCurrentGoalActivityListSuccess), payload: outputPayload })
  } catch (e: any) {
    console.error('GetActivityListFailed: ', e)

    if (e.status === 401) {
      const notification: INotification = {
        type: 'warning',
        content: t`Unauthorized!`,
      }
      yield put({ type: String(addNotification), payload: notification })
    }
  }
}

function* sagaGetCurrentCompanyGoal({ payload }: Action<any>) {
  try {
    const { id: goalId, token } = payload || {}
    // Leaderboard (with token auth).
    /*if (goalId && token) {
      yield put({ type: String(getCurrentLeaderboard), payload: { goalId, token } })
      return
    }*/

    // We should only load single goal
    // console.log('We should only load single goal', payload)
    if (goalId) {
      yield put({
        type: String(getSingleChallengeWithProgress),
        payload: {
          goalId,
          version,
          token,
        },
      })
    } else {
      // Create a new AbortController for this request
      if (challengesAbortController) {
        challengesAbortController.abort()
      }
      challengesAbortController = new AbortController()

      // If super admin is managing a company, pass companyId to load that company's challenges
      const superAdminTargetCompany: any = yield select((state: any) => state.superAdmin?.targetCompany)
      const requestPayload: any = { version }
      if (superAdminTargetCompany?.companyId) {
        requestPayload.companyId = superAdminTargetCompany.companyId
      }

      const { goals }: GetGoalsWithProgress$Response = yield call(
        api.activity.getChallengesWithProgress,
        requestPayload,
        { signal: challengesAbortController.signal },
      )

      // Reset the controller after successful completion
      challengesAbortController = null

      // Convert old collective challenges to new format
      const convertedGoals = goals ? goals.map(convertToMultipleGoalsFormat) : []
      yield put({ type: String(getCurrentCompanyGoalSuccess), payload: convertedGoals })
    }

    if (!token && payload && payload.updateList && payload.id) {
      yield put({
        type: String(getCurrentGoalActivityList),
        payload: { goalId: payload.id, companyId: payload.companyId },
      })
    }
  } catch (e: any) {
    // Do not report aborted requests as errors
    if (e.name === 'AbortError' || e.message === 'canceled') {
      console.log('GetChallengesWithProgress request was cancelled')
      return
    }

    console.error('GetCompanyGoalsFailed: ', e)
  }
}

function* sagaGetCurrentGoalExport({ payload }: Action<ExportGoalResults$Request>) {
  try {
    const { goalId } = payload
    yield put({ type: String(clearNotification) })
    yield put({ type: String(setLoadingExport) })

    const response: ExportGoalResults$Response = yield call(api.activity.exportGoalResults, {
      goalId,
      // Opt in to background exports — old clients omit this and get the synchronous export.
      supportsBackground: true,
    })

    // Background mode (backgroundExports company flag): the file arrives by email instead.
    if (response?.backgroundQueued) {
      const queuedNotification: INotification = {
        type: 'info',
        content: t`The export is being prepared — we will email you a download link when it is ready.`,
      }
      yield put({ type: String(addNotification), payload: queuedNotification })
      yield put({ type: String(setLoadingExportDone) })
      return
    }

    const { fileContent, fileMediaType, fileName } = response
    //const arrayBuffer = base64ToArrayBuffer(String(fileContent))
    download(String(fileName), String(fileContent), String(fileMediaType))

    yield put({ type: String(setLoadingExportDone) })
  } catch (e: any) {
    console.log('GetCurrentGoalExport failed: ', e)

    let msg: JSX.Element | string = getErrorMsg('export')

    if (e.status === 401) {
      msg = t`Unauthorized!`
    }

    const getUserActivityErrorNotification: INotification = {
      type: 'warning',
      content: msg,
    }

    yield put({ type: String(addNotification), payload: getUserActivityErrorNotification })
    yield put({ type: String(setLoadingExportDone) })
  }
}

function* sagaGetChatMessagesExport({ payload }: Action<any>) {
  try {
    yield put({ type: String(clearNotification) })
    const { fileContent, fileMediaType, fileName }: ExportChatMessages$Response = yield call(
      api.activity.exportChatMessages,
      {
        goalId: payload,
      },
    )
    //const arrayBuffer = base64ToArrayBuffer(String(fileContent))
    download(fileName, String(fileContent), fileMediaType)
  } catch (e: any) {
    console.log('GetChatMessagesExport failed: ', e)

    let msg: JSX.Element | string = getErrorMsg('chatExport')

    if (e.status === 401) {
      msg = t`Unauthorized!`
    }

    const notification: INotification = {
      type: 'warning',
      content: msg,
    }

    yield put({ type: String(addNotification), payload: notification })
  }
}

function* sagaGetChatImagesExport({ payload }: Action<any>) {
  try {
    yield put({ type: String(clearNotification) })
    yield put({ type: String(setLoadingChatImagesExport) })
    const response: ExportChatImages$Response | null = yield call(api.activity.exportChatImages, {
      goalId: payload,
      // Opt in to background exports — old clients omit this and get the synchronous export.
      supportsBackground: true,
    })

    // Background mode (backgroundExports company flag): the file arrives by email instead.
    if (response?.backgroundQueued) {
      const queuedNotification: INotification = {
        type: 'info',
        content: t`The export is being prepared — we will email you a download link when it is ready.`,
      }
      yield put({ type: String(addNotification), payload: queuedNotification })
      return
    }

    // Backend returns null/empty when the chat has no images to export.
    if (!response?.fileUrl) {
      const noImagesNotification: INotification = {
        type: 'info',
        content: t`There are no images to export.`,
      }
      yield put({ type: String(addNotification), payload: noImagesNotification })
      return
    }

    openUrl(response.fileUrl, true)
  } catch (e: any) {
    console.log('GetChatImagesExport failed: ', e)

    let msg: JSX.Element | string = getErrorMsg('chatExport')

    if (e.status === 401) {
      msg = t`Unauthorized!`
    }

    const notification: INotification = {
      type: 'warning',
      content: msg,
    }

    yield put({ type: String(addNotification), payload: notification })
  } finally {
    yield put({ type: String(setLoadingChatImagesExportDone) })
  }
}

export interface IJoinGoalPayload {
  goalId: GoalId
  progressType: ProgressType
  customType?: CustomType | null
  manualEntryEnabled?: boolean

  // For refreshChallengeResults.
  isPoints?: boolean
  pointsLogic?: string
  startDateFormatted?: string
  endDateFormatted?: string
  isLegacyRanking: boolean
}

function* sagaJoinGoal({ payload }: Action<IJoinGoalPayload>) {
  try {
    yield put({ type: String(setLoadingJoinGoal) })
    yield put({ type: String(clearNotification) })
    const {
      goalId,
      progressType,
      customType,
      manualEntryEnabled,
      isLegacyRanking,
      startDateFormatted,
      endDateFormatted,
    } = payload
    const { serviceProvider: serviceProviderRaw, dateOfBirth }: UserProfileDto = yield select(getUserDto)
    const userIntegrationScopes: string = yield select(getUserIntegrationScopes)

    const isStepChallenge = isStepsType(progressType)
    const isActivityIndex = isActivityIndexType(progressType)
    //const isDistance = isDistanceType(customType)

    /*if (
      isDistance &&
      serviceProviderRaw === ServiceProvider.GOOGLE_FIT &&
      !userIntegrationScopes.includes('fitness.location.read')
    ) {
      const serviceProvider = getServiceProviderLabel(serviceProviderRaw)
      const msg = t`We seem to have insufficient permissions to sync with your ${serviceProvider} integration.`
      const notification: INotification = {
        type: 'warning',
        //content: msg,
        content: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <span
              style={{
                margin: '10px 0',
                flexGrow: 1,
              }}
            >
              {msg}
            </span>{' '}
            <YumButton size="small" to={ROUTES.SETTINGS.to}>{t`Choose integration`}</YumButton>
          </div>
        ),
      }
      yield put({ type: String(addNotification), payload: notification })
      scrollToTop()
      return
    }*/

    const leanScope = !dateOfBirth
    const noIntegration = serviceProviderRaw === null

    const isManualEntryOnly = isManualEntryOnlyType(progressType, customType)
    const supportsManualEntry = manualEntryEnabled || isManualEntryOnly
    // console.log('supportsManualEntry', supportsManualEntry)
    // console.log('isManualEntryOnly', isManualEntryOnly)

    if (isActivityIndex && leanScope && (!noIntegration || manualEntryEnabled)) {
      const msg =
        currentLang === Lang.EN || currentLang === Lang.ET
          ? t`If you want to take part in this challenge, please provide additional data.`
          : t`If you want to take part in this challenge, please provide additional data and connect a heart rate activity device to the platform.`
      const notification: INotification = {
        type: 'warning',
        //content: msg,
        content: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <span
              style={{
                margin: '10px 0',
                flexGrow: 1,
              }}
            >
              {msg}
            </span>{' '}
            <YumSetupActivityDataWrapper />
          </div>
        ),
      }
      yield put({ type: String(addNotification), payload: notification })

      // Set loading.joinGoal to false
      yield put({ type: String(setLoadingJoinGoalDone) })

      scrollToTop()
      return
    }
    // No integration + manual entry disabled.
    else if (noIntegration && !supportsManualEntry) {
      const msg = t`Before you can join this challenge, you need to have a working integration.`
      const notification: INotification = {
        type: 'warning',
        //content: msg,
        content: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <span
              style={{
                margin: '10px 0',
                flexGrow: 1,
              }}
            >
              {msg}
            </span>{' '}
            <YumChooseIntegrationBtnWrapper buttonText={t`Choose integration`} />
          </div>
        ),
      }
      yield put({ type: String(addNotification), payload: notification })

      // Set loading.joinGoal to false
      yield put({ type: String(setLoadingJoinGoalDone) })

      scrollToTop()
      return
    }

    try {
      yield call(api.activity.joinGoal, { goalId })
    } catch (e: any) {
      console.error('JoinGoal failed: ', e)
      const msg =
        e?.message === 'userAlreadyInOverlappingChallenge'
          ? t`The challenge organizer only allows joining one challenge during this period. Leave your current challenge first to join another one.`
          : t`Joining failed. Please try again.`
      const notification: INotification = { type: 'warning', content: msg }
      yield put({ type: String(addNotification), payload: notification })
      yield put({ type: String(setLoadingJoinGoalDone) })
      scrollToTop()
      return
    }

    // Confirm the join. On the leaderboard view, keep it screen-reader-only — the
    // visual banner is undesired there. Elsewhere, show the success banner (which is
    // itself announced via its live region). WCAG 4.1.3.
    const joinedMessage = t`You have joined the challenge.`
    const pathname: string = yield select((state: any) => state.router?.location?.pathname || '')
    if (pathname.includes('/leaderboard')) {
      announce(joinedMessage, 'assertive')
    } else {
      const joinedNotification: INotification = {
        type: 'success',
        content: joinedMessage,
      }
      yield put({ type: String(addNotification), payload: joinedNotification })
    }

    // Handle old ranking vs new leaderboard.
    //if (!isLegacyRanking) {
    // We should only download single challenge with progress
    // yield put({
    //   type: String(getSingleChallengeWithProgress),
    //   payload: {
    //     goalId,
    //     version,
    //   },
    // })
    // console.log('TODO: We should also request daily data')
    // yield put({
    //   type: String(refreshChallengeResults),
    //   payload: {
    //     goalId,
    //     customType: getCustomTypeWithLegacy(customType || null, progressType),
    //     startDate: startDateFormatted,
    //     endDate: endDateFormatted,
    //   },
    // })
    // refreshChallengeResults({
    //   goalId,
    //   customType: getCustomTypeWithLegacy(customType, defaultProgressType),
    //   startDate: resolvedStartDate,
    //   endDate: resolvedEndDate,
    //   participating: resolvedParticipating,
    //   isCollective,
    //   preventListUpdate: !isLegacyRanking,
    //   isActivityIndex: isAnyActivityIndex,
    //   manualEntryEnabled: resolvedManualEntryEnabled,
    //   // TODO:
    //   pointsLogicId: null,
    //   pointsLogic: resolvedPointsLogic,
    //   isPoints: resolvedIsPoints,
    //   isMeView,
    //   groupSum,
    //   token,
    //   oldSplitRankingView: isLegacyRanking && !splitRankingView,
    // }),
    //} else {
    const { goals }: GetGoalsWithProgress$Response = yield call(api.activity.getChallengesWithProgress, { version })
    // Convert old collective challenges to new format
    const convertedGoals = goals ? goals.map(convertToMultipleGoalsFormat) : []
    yield put({ type: String(getCurrentCompanyGoalSuccess), payload: convertedGoals })

    const { isPoints, pointsLogic } = payload
    const isAnyActivityIndex = [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType)
    const isCollective = isCollectiveType(progressType)
    console.log('refreshChallengeResults sagaJoinGoal', 1)
    yield put({
      type: String(refreshChallengeResults),
      payload: {
        goalId,
        customType: getCustomTypeWithLegacy(customType || null, progressType),
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        participating: true,
        isCollective,
        preventListUpdate: false,
        isActivityIndex: isAnyActivityIndex,
        manualEntryEnabled,
        pointsLogic,
        isPoints,
        isMeView: true,
        //groupSum,
        //token,
        oldSplitRankingView: isLegacyRanking, //&& !splitRankingView,
      },
    })
    //}

    // yield put({ type: String(joinGoalSuccess), payload: { goalId } })
    yield put({ type: String(setLoadingJoinGoalDone) })
  } catch (e: any) {
    console.error('JoinGoal post-join refresh failed: ', e)
    yield put({ type: String(setLoadingJoinGoalDone) })
  }
}

function* sagaLeaveGoal({ payload }: Action<LeaveGoal$Request>) {
  try {
    yield put({ type: String(clearNotification) })
    yield call(api.activity.leaveGoal, payload)

    // Visible confirmation, announced by the Notification live region (WCAG 4.1.3).
    const notification: INotification = {
      type: 'success',
      content: t`You have left the challenge.`,
    }
    yield put({ type: String(addNotification), payload: notification })

    const { goals }: GetGoalsWithProgress$Response = yield call(api.activity.getChallengesWithProgress, { version })
    // Convert old collective challenges to new format
    const convertedGoals = goals ? goals.map(convertToMultipleGoalsFormat) : []
    yield put({ type: String(getCurrentCompanyGoalSuccess), payload: convertedGoals })
  } catch (e: any) {
    console.error('LeaveGoal failed: ', e)
  }
}

/**
 * Determines the groupSum value for a given goal.
 * Uses the goal's activity view and config to extract the correct groupSum.
 * Falls back to isCollectiveChallenge if no specific value is found.
 *
 * @param goalId - The goal ID (can be number or string, will be converted to string for selectors)
 * @returns The groupSum value, or undefined if not determinable
 */
function* determineGroupSum(goalId: GoalId) {
  const goal: GoalWithProgress = yield select(getUserGoal, goalId) as unknown as GoalWithProgress

  if (!goal?.progressType) {
    return true
  }

  // avgAgg is a goal-level setting that determines aggregation mode, so prefer it
  // over the activityView fallback (which is unset on fresh navigation and would
  // otherwise default to true, returning SUM for an AVG goal).
  if (goal.goals !== undefined && goal.avgAgg !== undefined) {
    return !goal.avgAgg
  }

  const activityView: ActivityView | null = yield select(getActivityView, goalId) as unknown as ActivityView | null
  if (!activityView) {
    return true
  }

  // Fall back to selector, then to collective challenge flag
  const isCollectiveChallenge = isCollectiveType(goal.progressType)
  const selectorGroupSum: boolean | null = yield select(getGroupSum, goalId) as unknown as boolean | null
  return selectorGroupSum ?? isCollectiveChallenge
}

function* sagaGetSingleChallengeWithProgress({ payload }: Action<GetSingleChallengeWithProgress$Request>) {
  try {
    if (!payload.version) payload.version = version
    // Determine groupSum if not already provided
    if (payload.groupSum === undefined) {
      payload.groupSum = yield call(determineGroupSum, payload.goalId)
    }
    const { goal, divisionDtos, companyCountries, divisionCategories }: GetSingleChallengeWithProgress$Response =
      yield call(api.activity.getSingleChallengeWithProgress, payload)
    // Convert old collective challenge to new format
    const convertedGoal = convertToMultipleGoalsFormat(goal)
    yield put({ type: String(getSingleChallengeWithProgressSuccess), payload: { goal: convertedGoal } })

    if (payload.token) {
      yield put({ type: String(getCompanyDivisionsSuccess), payload: divisionDtos })
      yield put({ type: String(getDivisionCategoriesSuccess), payload: divisionCategories })
      yield put({ type: String(getUserCompanyCountriesSuccess), payload: companyCountries })
    }
  } catch (e: any) {
    console.error('sagaGetSingleChallengeWithProgress failed: ', e)
  }
}

function* sagaCancelGetChallengesWithProgress() {
  if (challengesAbortController) {
    challengesAbortController.abort()
    challengesAbortController = null
    console.log('Cancelled getChallengesWithProgress request')
  }
  yield
}

export function* activityManagementSagas() {
  yield all([
    takeEvery(String(getCurrentGoalActivityList), sagaGetCurrentGoalActivityList),
    takeEvery(String(getCurrentLeaderboard), sagaGetCurrentLeaderboard),
    takeEvery(String(getCurrentCompanyGoal), sagaGetCurrentCompanyGoal),
    takeEvery(String(getCurrentGoalExport), sagaGetCurrentGoalExport),
    takeEvery(String(getChatMessagesExport), sagaGetChatMessagesExport),
    takeEvery(String(getChatImagesExport), sagaGetChatImagesExport),
    takeEvery(String(joinGoal), sagaJoinGoal),
    takeEvery(String(leaveGoal), sagaLeaveGoal),
    takeEvery(String(getSingleChallengeWithProgress), sagaGetSingleChallengeWithProgress),
    takeEvery(String(cancelGetChallengesWithProgress), sagaCancelGetChallengesWithProgress),
  ])
}
